import { createContext, useContext, type ReactNode } from 'react'
import { type Feature, featureFlags, isFeatureEnabled } from '@/utils/feature-flag'

interface FeatureFlagContextValue {
  isFeatureEnabled: (feature: Feature) => boolean
  features: Record<Feature, boolean>
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined)

interface FeatureFlagProviderProps {
  children: ReactNode
}

/**
 * Feature Flag Provider
 * Provides feature flag values throughout the application
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const value: FeatureFlagContextValue = {
    isFeatureEnabled,
    features: featureFlags
  }

  return (
    <FeatureFlagContext.Provider value={value}> {children} </FeatureFlagContext.Provider>
  )
}

/**
 * Hook to access feature flags
 * @example
 * const { isFeatureEnabled } = useFeatureFlags()
 * if (isFeatureEnabled(Feature.FLEET)) {
 *   // Show fleet features
 * }
 */
export function useFeatureFlags(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext)

  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider')
  }

  return context
}
